export const AUTH_DIAGNOSTIC_CODES = Object.freeze({
  tokenMissing: 'AUTH-01',
  tokenInvalid: 'AUTH-02',
  storageUnavailable: 'AUTH-03',
  storageMismatch: 'AUTH-04',
  immediateUnauthorized: 'AUTH-05',
})

const RECENT_TOKEN_WRITE_KEY = 'auth:recentAccessTokenWrite'
const PENDING_DIAGNOSTIC_KEY = 'auth:pendingDiagnostic'
const MIN_ACCESS_TOKEN_LENGTH = 65
const RECENT_AUTH_WINDOW_MS = 60 * 1000

let recentTokenWrite
let pendingDiagnostic

function getSanitizedPage () {
  if (typeof location === 'undefined') return ''
  return `${location.pathname}${String(location.hash || '').split('?')[0]}`
}

function getEnvironment (context = {}) {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  return {
    project: context.project || 'unknown',
    source: context.source || 'unknown',
    inApp: context.inApp || false,
    isAndroid: /Android/i.test(userAgent),
    isSamsung: /SamsungBrowser|SM-[A-Z0-9]/i.test(userAgent),
    userAgent,
    page: getSanitizedPage(),
  }
}

function createDiagnostic (code, context = {}, details = {}) {
  return {
    code,
    occurredAt: new Date().toISOString(),
    ...getEnvironment(context),
    ...details,
  }
}

function removeInvalidAccessToken () {
  try {
    localStorage.removeItem('accessToken')
  } catch (error) {
    // 存储不可用时无法清理，保留原始诊断结果。
  }
}

function writeSessionJson (key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
    return true
  } catch (error) {
    return false
  }
}

function readSessionJson (key) {
  try {
    const value = sessionStorage.getItem(key)
    return value ? JSON.parse(value) : null
  } catch (error) {
    return null
  }
}

function removeSessionItem (key) {
  try {
    sessionStorage.removeItem(key)
  } catch (error) {
    // sessionStorage 不可用时仍可使用当前页面的内存记录。
  }
}

function markRecentTokenWrite (context, tokenLength) {
  recentTokenWrite = {
    storedAt: Date.now(),
    tokenLength,
    ...getEnvironment(context),
  }
  writeSessionJson(RECENT_TOKEN_WRITE_KEY, recentTokenWrite)
}

/**
 * 写入并立即回读 accessToken。返回的诊断信息只包含长度和运行环境，不包含 token 原文。
 */
export function persistAccessToken (accessToken, context = {}) {
  const tokenLength = typeof accessToken === 'string' ? accessToken.length : 0

  if (!tokenLength) {
    return {
      ok: false,
      diagnostic: createDiagnostic(AUTH_DIAGNOSTIC_CODES.tokenMissing, context, {
        reason: 'token_missing',
        tokenLength,
        storedTokenLength: 0,
      }),
    }
  }

  let storedToken
  try {
    localStorage.setItem('accessToken', accessToken)
    storedToken = localStorage.getItem('accessToken')
  } catch (error) {
    removeInvalidAccessToken()
    return {
      ok: false,
      diagnostic: createDiagnostic(AUTH_DIAGNOSTIC_CODES.storageUnavailable, context, {
        reason: 'local_storage_unavailable',
        tokenLength,
        storedTokenLength: 0,
        storageErrorName: error && error.name ? error.name : 'Error',
      }),
    }
  }

  if (storedToken !== accessToken) {
    const storedTokenLength = typeof storedToken === 'string' ? storedToken.length : 0
    removeInvalidAccessToken()
    return {
      ok: false,
      diagnostic: createDiagnostic(AUTH_DIAGNOSTIC_CODES.storageMismatch, context, {
        reason: 'local_storage_readback_mismatch',
        tokenLength,
        storedTokenLength,
      }),
    }
  }

  if (tokenLength < MIN_ACCESS_TOKEN_LENGTH) {
    removeInvalidAccessToken()
    return {
      ok: false,
      diagnostic: createDiagnostic(AUTH_DIAGNOSTIC_CODES.tokenInvalid, context, {
        reason: 'token_length_invalid',
        tokenLength,
        storedTokenLength: storedToken.length,
      }),
    }
  }

  markRecentTokenWrite(context, tokenLength)
  return {
    ok: true,
    tokenLength,
  }
}

function getRecentTokenWrite () {
  return recentTokenWrite || readSessionJson(RECENT_TOKEN_WRITE_KEY)
}

function clearRecentTokenWrite () {
  recentTokenWrite = null
  removeSessionItem(RECENT_TOKEN_WRITE_KEY)
}

function sanitizeRequestPath (url) {
  return String(url || '').split('?')[0].slice(0, 200)
}

export function createImmediateUnauthorizedDiagnostic ({
  project,
  inApp,
  requestUrl,
  requestStartedAt,
  hadAuthorization,
  now = Date.now(),
} = {}) {
  const marker = getRecentTokenWrite()
  if (!marker || !marker.storedAt || now - marker.storedAt > RECENT_AUTH_WINDOW_MS) {
    clearRecentTokenWrite()
    return null
  }
  if (!hadAuthorization || (requestStartedAt && requestStartedAt < marker.storedAt)) {
    return null
  }
  clearRecentTokenWrite()

  return createDiagnostic(AUTH_DIAGNOSTIC_CODES.immediateUnauthorized, {
    project: project || marker.project,
    source: marker.source,
    inApp: inApp || marker.inApp,
  }, {
    reason: 'immediate_401_after_token_write',
    tokenLength: marker.tokenLength,
    storedTokenLength: marker.tokenLength,
    millisecondsAfterWrite: Math.max(0, now - marker.storedAt),
    requestPath: sanitizeRequestPath(requestUrl),
  })
}

export function queueAuthDiagnostic (diagnostic) {
  pendingDiagnostic = diagnostic
  return writeSessionJson(PENDING_DIAGNOSTIC_KEY, diagnostic)
}

export function takePendingAuthDiagnostic () {
  const diagnostic = pendingDiagnostic || readSessionJson(PENDING_DIAGNOSTIC_KEY)
  pendingDiagnostic = null
  removeSessionItem(PENDING_DIAGNOSTIC_KEY)
  return diagnostic
}
